import React from 'react';
import { Button, MoneyInput, Alert } from '@parsina/framework';
import { C } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { type } from '../../constants/typography.js';
import { fmtCoins } from '../../constants/creditPricing.js';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { gatewayLabel } from '../../constants/paymentGateway.js';
import { CoinGlyph } from '../../components/common/CoinChip.jsx';

// خرید پارسه — the body of the top-up flow.
//
// ONE component for both surfaces: it is rendered inside a `Dialog` on the
// desktop and a `BottomSheet` on the phone, and neither owns any of the choice
// logic. A second mobile copy would be a second place for the price of a
// package to live.
//
// PACKAGES ARE EXPLAINED IN WORK DONE, not in money — «≈ ۲۰ جلسه با دستیار».
// Toman appears twice and only where the number really is a price: on each
// row's trailing edge, and on the pay button.
//
// ── WHY A RADIOGROUP AND NOT FOUR onClick DIVS ─────────────────────────────
// This is one choice among four, which is what a radio group IS: arrow keys
// move between the options, the selection is announced, and `aria-checked`
// says which one is live. The doctor panel's version nests its amount field
// INSIDE the custom row's <button>, which is invalid (interactive content in a
// button) and leaves the field unreachable by keyboard — so here the field sits
// below the row instead, where it is also a full-width target on a phone.

const PACKAGES = [200, 500, 1000];

// The floor is ONE پارسه (`minToman` below). It exists to stop trivially small
// gateway transactions, not to force a package: a dentist who wants a single
// پارسه's worth should be able to buy it, and every Iranian gateway settles
// amounts this size happily.

// What one session costs when there is no history to average — roughly the
// differential plus a note refine plus an interaction check, i.e. "used the
// assistant properly, once".
const FALLBACK_COST_PER_VISIT = 23;

/**
 * @param avgPerVisit  the dentist's own پارسه/session, or null → the fallback
 * @param onPay        (coins, toman) => begins payment; the caller redirects
 */
export default function TopUpSheet({ avgPerVisit, onPay, busy = false, error = null }) {
  const { t, formatNumber } = useTranslation();
  // What one پارسه costs in Toman: the active policy's figure, else the constant.
  const { coinPriceToman: COIN_PRICE_TOMAN } = useCreditPrices();
  const minToman = COIN_PRICE_TOMAN;
  const [selected, setSelected] = React.useState(500); // پارسه, or 'custom'
  const [customToman, setCustomToman] = React.useState('');

  const perVisit = avgPerVisit && avgPerVisit > 0 ? avgPerVisit : FALLBACK_COST_PER_VISIT;
  const isCustom = selected === 'custom';
  const customCoins = Math.floor((Number(customToman) || 0) / COIN_PRICE_TOMAN);
  const coins = isCustom ? customCoins : selected;
  const toman = coins * COIN_PRICE_TOMAN;
  const belowMin = toman < minToman;

  const options = [...PACKAGES, 'custom'];

  // Arrow keys move the selection, which is what makes this a radio group
  // rather than four buttons that happen to look like one.
  const onKeyDown = (event) => {
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const back = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    if (!forward && !back) return;
    event.preventDefault();
    const at = options.indexOf(selected);
    const next = forward
      ? options[(at + 1) % options.length]
      : options[(at - 1 + options.length) % options.length];
    setSelected(next);
  };

  const Row = ({ value, children }) => {
    const active = selected === value;
    return (
      <Button
        type="button"
        role="radio"
        aria-checked={active}
        tabIndex={active ? 0 : -1}
        variant={active ? 'subtle' : 'outline'}
        onClick={() => setSelected(value)}
        data-testid={`topup-${value === 'custom' ? 'custom' : `pkg-${value}`}`}
        className="min-h-11 w-full justify-start gap-3 rounded-xl px-4 py-3 h-auto"
      >
        {children}
      </Button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        role="radiogroup"
        aria-label={t('credits.topUp.chooseAmount')}
        onKeyDown={onKeyDown}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {PACKAGES.map((pkg) => (
          <Row key={pkg} value={pkg}>
            <span style={{ color: C.teal, display: 'flex' }}><CoinGlyph size={16} /></span>
            <span style={{ flex: 1, textAlign: 'start', minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...type.amount, color: C.textPrimary }}>
                  {fmtCoins(pkg, formatNumber)} {t('credits.unit')}
                </span>
                {pkg === 500 && (
                  <span style={{ ...type.micro, fontWeight: 800, color: C.green, background: C.greenLight, borderRadius: 999, padding: '1px 8px' }}>
                    {t('credits.topUp.recommended')}
                  </span>
                )}
              </span>
              <span style={{ ...type.caption, color: C.textMuted, display: 'block', marginTop: 2 }}>
                {t('credits.topUp.approxVisits', { count: formatNumber(Math.round(pkg / perVisit)) })}
              </span>
            </span>
            <span style={{ ...type.label, fontWeight: 700, color: C.textSecondary, whiteSpace: 'nowrap' }}>
              {formatNumber(pkg * COIN_PRICE_TOMAN)} {t('credits.tomanShort')}
            </span>
          </Row>
        ))}

        <Row value="custom">
          <span style={{ color: isCustom ? C.teal : C.textMuted, display: 'flex' }}>
            <CoinGlyph size={16} />
          </span>
          <span style={{ flex: 1, textAlign: 'start', ...type.body, color: C.textPrimary }}>
            {t('credits.topUp.custom')}
          </span>
          {isCustom && coins > 0 && (
            <span style={{ ...type.caption, color: C.textMuted, whiteSpace: 'nowrap' }}>
              = {fmtCoins(coins, formatNumber)} {t('credits.unit')}
            </span>
          )}
        </Row>
      </div>

      {/* OUTSIDE the row, for the reason in the header: a field nested in a
          button is invalid and unreachable by keyboard. Full width, so it is
          also a real target on a phone. */}
      {isCustom && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...type.label, color: C.textSecondary, whiteSpace: 'nowrap' }}>
            {t('credits.topUp.customAria')}
          </span>
          {/* The unit rides inside the field now — the trailing «تومان» span it
              used to need is the component's own chip. */}
          <MoneyInput
            value={customToman}
            onChange={setCustomToman}
            autoFocus
            data-testid="topup-custom-input"
            unitLabel={t('credits.toman')}
            style={{ flex: 1, minWidth: 0 }}
            inputStyle={{
              ...type.body,
              width: '100%',
              boxSizing: 'border-box',
              minHeight: '2.75rem',
              padding: '0 12px',
              paddingInlineStart: 52,
              textAlign: 'end',
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              background: C.card,
              color: C.textPrimary,
              outline: 'none',
            }}
          />
        </label>
      )}

      {error && (
        <Alert variant="error" data-testid="topup-error">
          {/* The server's own message when there is one: a gateway that refused
              the amount, a wallet that is down and a network blip are three
              different instructions to the dentist, and one generic line over
              all of them says a working feature does not exist. */}
          {error.message && error.message !== 'NO_URL'
            ? t('credits.topUp.failedWithReason', { reason: error.message })
            : t('credits.topUp.failed')}
        </Alert>
      )}

      <Button
        onClick={() => !belowMin && !busy && onPay(coins, toman)}
        disabled={belowMin || busy}
        data-testid="topup-pay"
        className="w-full min-h-11"
        style={{ marginTop: 4 }}
      >
        {busy
          ? t('credits.topUp.redirecting')
          : toman > 0
            ? t('credits.topUp.payVia', { amount: formatNumber(toman), gateway: gatewayLabel(t) })
            : t('credits.topUp.enterAmount')}
      </Button>

      <div style={{ ...type.micro, color: C.textMuted, textAlign: 'center', lineHeight: 1.8 }}>
        {belowMin
          ? t('credits.topUp.minimum', { amount: formatNumber(minToman) })
          : t('credits.topUp.redirectNote')}
      </div>
    </div>
  );
}
